import {
  IBookingAvailabilityService,
  ILayoutMinimalABC,
  IRecurringBookingScheduleSubset,
  ITableItemABC,
  ITablePickerData,
  ITableSeaterRecurringProps,
} from './type';
import { cloneDeep, isEmpty, uniqBy } from 'lodash-es';
import TableSelectionService from 'external-services/table-selection-service/index';
import {
  BlockoutType,
  BookingMethods,
  IBlockout,
  ILayoutMinimal,
  ITableItem,
  ITableSectionList,
  SECTION_ANY_ID,
} from 'shared-types/SharedTypes';
import DateUtilsService from 'external-services/date-utils-service';
import moment from 'moment';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import SectionLookupService from 'external-services/section-lookup-service/sectionLookup.service';
import RecurringBookingService from 'external-services/recurring-booking-service';
import { ITableSelectionListItem } from './type';
const NS = 'RecurringTableSeaterService';

class RecurringTableSeaterService {
  private bookingAvailabilityService: IBookingAvailabilityService;

  setup(bookingAvailabilityService: IBookingAvailabilityService): void {
    this.bookingAvailabilityService = bookingAvailabilityService;
  }

  async getTableSeaterInfo(
    tableSeaterProps: ITableSeaterRecurringProps,
    serviceId: string,
    time: string,
    numOfPeople: number,
    preferredSectionId = SECTION_ANY_ID,
    isVenueOpen = true
  ) {
    const tablePickerResponse = await RecurringBookingService.getTablePicker(
      serviceId,
      time,
      numOfPeople
    );
    const pickerTime = DateUtilsService.dateStrToDateTime(time);
    const newSchedule: IRecurringBookingScheduleSubset = {
      services: [tablePickerResponse.service],
      blockouts: tablePickerResponse.blockouts,
    };

    const { tableSelectionData, sectionsLookup, autoSelectTableId, isEvent } =
      this.getTableSelectionData(
        tablePickerResponse,
        numOfPeople,
        newSchedule,
        pickerTime,
        false,
        preferredSectionId,
        tableSeaterProps.preselectedTableIds
      );

    const bookingStart = moment(time).toDate();
    const tableSelectionList: ITableItem[] = cloneDeep(tableSelectionData.tableSelectionList);
    tableSelectionList.forEach((table) => {
      if (!isVenueOpen) {
        table.free = false;
        table.valid = false;
      } else {
        if (
          this.blockoutsWithinSelectedBookingTime(
            newSchedule.blockouts,
            bookingStart,
            isEvent
          ).some((b) => this.isBlockedOut(table, b))
        ) {
          table.free = false;
          table.valid = false;
        }
      }
    });
    const tableSelectionListWithJoins: ITableItem[] = cloneDeep(
      tableSelectionData.tableSelectionListWithJoins
    );
    tableSelectionListWithJoins.forEach((table) => {
      if (!isVenueOpen) {
        table.valid = false;
        table.free = false;
      } else {
        if (
          this.blockoutsWithinSelectedBookingTime(
            newSchedule.blockouts,
            bookingStart,
            isEvent
          ).some((b) => this.isBlockedOut(table, b))
        ) {
          table.free = false;
          table.valid = false;
        }
      }
    });
    return {
      ...tableSeaterProps,
      tableSelectionList: tableSelectionList,
      tableSelectionListWithJoins: tableSelectionListWithJoins,
      sectionsLookup,
      preselectedTableIds: !isEmpty(autoSelectTableId)
        ? autoSelectTableId
        : [tableSelectionData.tableSelectionList[0]?._id],
    };
  }

  /**
   * This function was copied from Group Widget `src/app/services/helpers/stateHelper.service.ts`
   * and at some point should be added to the "table-selection-service" submodule, so it can
   * be shared between diary and group widget.
   */

  private getTableSelectionData(
    data: ITablePickerData,
    covers: number,
    selectedSchedule: IRecurringBookingScheduleSubset,
    bookingTimeAsDate: Date,
    isWalkIn: boolean,
    preferredSectionId = SECTION_ANY_ID,
    preselectedTableIds: string[]
  ): {
    tableSelectionData: ITableSectionList;
    sectionsLookup: any;
    autoSelectTableId: string[];
    isEvent: boolean;
  } {
    const { times } = selectedSchedule.services.find((s) => s.id && s.id === data.service.id);
    const sectionsLookup = this.getSectionsLookUp(selectedSchedule, data.service.layouts);
    const tables: ITableItemABC[] = data.service.layouts.reduce((acc, lo) => {
      if (lo.tables) {
        acc = acc.concat(lo.tables.reverse());
      }
      return acc;
    }, []);
    // just used a subset of properties, since the full interfaces differ between diary and group widget
    const bookingsByTable = this.bookingAvailabilityService.getBookingsByTable(
      data.bookings.map(({ tables, status, duration, time, serviceId, _id, people }) => {
        return {
          _id,
          serviceId,
          people,
          tables,
          status,
          duration,
          time: DateUtilsService.dateStrToDateTime(time),
        } as any;
      })
    );

    data.service.times = times;

    const layouts: ILayoutMinimalABC[] = cloneDeep(data.service.layouts);
    layouts.forEach((l) => {
      l.tablesJoins.forEach((tj) => {
        (tj as unknown as ITableItem).tables = (tj.tables as unknown as ITableItemABC[]).map(
          (t) => t._id
        ) as string[];
      });
    });

    // ABC sets `tables` as objects, whereas diary sets them as ids
    const layoutsWithTablesAsIds = layouts as unknown as ILayoutMinimal[];

    const selectedTime = this.bookingAvailabilityService.checkBookingsAtTime(
      bookingTimeAsDate,
      bookingTimeAsDate,
      selectedSchedule,
      data.service,
      layoutsWithTablesAsIds,
      data.service.duration,
      covers,
      tables,
      bookingsByTable,
      preferredSectionId,
      null,
      true,
      isWalkIn ? BookingMethods.walkin : BookingMethods.phone,
      false
    );

    let autoSelectTableId = [];
    const tableSelectionData = TableSelectionService.createTableSectionList(
      selectedTime,
      tables,
      sectionsLookup
    );
    // to check if the preselected table is present in the tableList
    const isPreSelectedTableAvailable =
      !isEmpty(tableSelectionData.tableSelectionList) && !isEmpty(preselectedTableIds)
        ? preselectedTableIds.some((id) =>
            tableSelectionData.tableSelectionList.find((t) => t._id === id)
          )
        : false;
    const selectedTable = TableSelectionService.getTableByTime(selectedTime);

    // if there are any errors then selectedTable returns null - we should then send the preselectedTableIds if they exist
    if (selectedTime.errorMgs.length > 0) {
      autoSelectTableId = preselectedTableIds || [];
    } else {
      autoSelectTableId =
        !isEmpty(preselectedTableIds) && isPreSelectedTableAvailable
          ? preselectedTableIds
          : this.checkForJoinedTables(selectedTable);
    }

    const isEvent = data.service.id.indexOf('event_') === 0;

    return { tableSelectionData, sectionsLookup, autoSelectTableId, isEvent };
  }

  private checkForJoinedTables(selectedTable: ITableItem): string[] {
    if (!isEmpty(selectedTable?.tables)) {
      return selectedTable.tables;
    }
    return [selectedTable?._id] || [];
  }

  /**
   * Returns all the sections
   */
  getSectionsLookUp(
    schedule: IRecurringBookingScheduleSubset,
    layouts: { sectionId: string }[]
  ): any {
    let allSections = schedule.services.reduce((acc, service) => {
      return acc.concat(service.sections);
    }, []);

    allSections = uniqBy(allSections, 'id');

    let sectionsLookup: any = {};

    layouts.forEach((lo) => {
      const section = allSections.find((section) => lo.sectionId === section?.id);
      if (section) {
        sectionsLookup[lo.sectionId] = section;
      }
    });

    sectionsLookup = SectionLookupService.manageDuplicateOrder(sectionsLookup);

    return sectionsLookup;
  }

  /**
   * Filters blockouts to be within the start time and duration of the booking.
   */
  private blockoutsWithinSelectedBookingTime(
    blockouts: IBlockout[],
    bookingStart: Date,
    selectedServiceIsEvent: boolean
  ): IBlockout[] {
    return blockouts.filter(({ startDateTime, endDateTime, type }) => {
      const boStart = moment(startDateTime).toDate();
      const boEnd = moment(endDateTime).toDate();
      const boStartsBeforeOrOnBooking = boStart <= bookingStart;
      const boEndsAfterBookingBegins = boEnd > bookingStart;

      /**
       * Don't include events as blockouts if the current service is an event.
       * We don't have to worry about other events blockout out because they cannot
       * overlap each other anyway.
       */
      if (selectedServiceIsEvent && type === BlockoutType.event) {
        return false;
      }

      return boStartsBeforeOrOnBooking && boEndsAfterBookingBegins;
    });
  }

  /**
   * Checks if the table is attached to the blockout
   */
  isBlockedOut(t: ITableSelectionListItem, b: IBlockout) {
    if (b.closed) {
      return true;
    }
    const sectionIsBlocked = b.blockedSections.some((bs) => {
      const sectionId = t.sectionId || (t.tablesList?.length && t.tablesList[0].sectionId);
      return bs === sectionId;
    });
    if (sectionIsBlocked) return true;

    const tableIsBlocked = b.blockedTables.some((bt) => {
      if (t.tables?.length) {
        return t.tables.some((tId) => tId === bt._id);
      }
      return bt._id === t._id;
    });
    return tableIsBlocked;
  }
}

const instance = new RecurringTableSeaterService();
export default instance as RecurringTableSeaterService;
